"use client";

import { decodeJwt } from "jose";

// Define locally to avoid importing server-side @texturehq/authentication package
// which pulls in Express and Node.js 'fs' module incompatible with client bundles
const SUPER_ADMIN_CLAIM_KEY = "https://texture.energy/superAdmin";

const TOKEN_STORAGE_KEY = "accessToken";
const REFRESH_TOKEN_STORAGE_KEY = "refreshToken";
const ID_TOKEN_STORAGE_KEY = "idToken";

/**
 * Sets the access token in localStorage only
 * NOTE: Cookies are set server-side as httpOnly for security
 * This function only handles client-side localStorage for GraphQL client access
 */
export const setAccessToken = (token: string | null): void => {
  if (typeof window === "undefined") return;

  if (token && token !== "undefined") {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } else {
    // Clear token if it's not valid
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }
};

/**
 * Sets the refresh token in localStorage only
 * NOTE: Cookies are set server-side as httpOnly for security
 * This function only handles client-side localStorage for token refresh operations
 */
export const setRefreshToken = (token: string | null): void => {
  if (typeof window === "undefined") return;

  if (token && token !== "undefined") {
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
  } else {
    // Clear refresh token if it's not valid
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  }
};

/**
 * Sets the ID token in localStorage
 */
export const setIdToken = (token: string | null): void => {
  if (typeof window === "undefined") return;

  if (token && token !== "undefined") {
    localStorage.setItem(ID_TOKEN_STORAGE_KEY, token);
  } else {
    localStorage.removeItem(ID_TOKEN_STORAGE_KEY);
  }
};

/**
 * Gets the access token from localStorage
 */
export const getAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;
  const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
  return storedToken && storedToken !== "undefined" ? storedToken : null;
};

/**
 * Clears all auth tokens from localStorage
 * NOTE: httpOnly cookies are cleared server-side via /api/auth/logout
 */
export const clearAllTokens = (): void => {
  if (typeof window === "undefined") return;

  // Clear from localStorage
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(ID_TOKEN_STORAGE_KEY);
};

/**
 * Checks if the JWT token is valid and not expired
 */
export const isTokenValid = (token: string | null): boolean => {
  if (!token || token === "undefined") return false;
  try {
    const decodedToken = decodeJwt(token);
    const dateNow = Date.now() / 1000;
    return (decodedToken.exp ?? 0) > dateNow;
  } catch {
    return false;
  }
};

/**
 * Checks if the token contains the super admin claim
 */
export const isSuperAdmin = (token: string | null): boolean => {
  if (!token || token === "undefined") return false;

  try {
    const decoded = decodeJwt(token);
    return decoded[SUPER_ADMIN_CLAIM_KEY] === true;
  } catch {
    return false;
  }
};
